import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

// --- Interfaces ---

export interface EventCategory {
  id: number;
  categoryName: string;
  creationDate?: string;
  description?: string;
  modifiedDate?: string;
  active?: boolean;
}

export interface Grade {
  gradeid: number;
  digiRef: number;
}

export interface Batch {
  id: number;
  batchname: string;
  grade?: Grade;
  idserv?: number;
  description?: string;
}

export interface Organization {
  id: number;
  nameorg: string;
  type: string;
  facebookOrganization: string;
  frGouvernorat: string;
  arGouvernorat: string;
  arabeNameorg: string;
  frAdress: string;
  arAdress: string;
  kissati: string;
  comments: string;
}

export interface Departement {
  id: number;
  nameDepartement: string;
  description: string;
  creattionDate: string;
  organization: Organization;
  active: boolean;
}

export interface Event {
  id: number;
  eventName: string;
  nbrplace: number;
  resteplace: number;
  description: string;
  creationDate?: string;
  startDate: string;
  endDate: string;
  eventCategory: EventCategory;
  batchs: { batch: Batch[] };
  pathImage?: string;
  pathProgram?: string;
  eventNotification?: boolean;
  active: boolean;
  role: number;
  departements?: { departement: Departement[] };
  anyNs?: string[];
  any?: string[];
}

export interface Reservation {
  id: number;
  event: Event;
  studentId: number;
  guardianId: number;
  creationDate: string;
  description?: string;
}

export interface EventResponse {
  error?: string;
  response: Event[];
  totalPages?: number;
}

// --- Service principal ---

@Injectable({
  providedIn: 'root'
})
export class EventService {

  private readonly apiBaseUrl = environment.apiUrl.replace(/\/$/, '');
  private readonly apiOrigin = this.apiBaseUrl.replace(/\/api$/, '');
  private readonly DOMAINE_URL = `${this.apiBaseUrl}/event/`;

  constructor(private http: HttpClient) {}

  // 🔹 Ajouter une réservation à un événement
  addReservation(
    idEvent: number,
    idStudent: number,
    idGuardian: number,
    idUser: number
  ): Observable<string> {
    if (!idEvent || !idStudent || !idGuardian || !idUser) {
      return throwError(() => new Error('Tous les IDs doivent être renseignés.'));
    }

    const url = `${this.DOMAINE_URL}addReservationEvent/${idStudent}/${idEvent}/${idGuardian}/${idUser}`;
    return this.http.get<{ response: string }>(url).pipe(
      map(res => res.response),
      catchError(() =>
        throwError(() => new Error('Erreur lors de la réservation.'))
      )
    );
  }

  // 🔹 Rechercher les événements liés à un élève
 searchStudentEvents(
  idStudent: number,
  nameOrg: string,
  idBatch: number,
  numberPage: number = 1,
  sizePage: number = 10
): Observable<{ events: Event[], totalPages: number, totalCount?: number }> {
  const url = `${this.DOMAINE_URL}searchStudent`;

  const body = {
    idStudent,
    nameOrg,
    idBatch,
    numberPage,
    sizePage
  };

  return this.http.post<any>(url, body).pipe(
    map(res => {
      console.log('Full API Response:', res);
     
      if (res && Array.isArray(res.response)) {
        const events = res.response.map((event: any) => ({
          ...event,
          // 🧩 Nettoyage : transformer les URLs relatives en URLs complètes
          pathImage: this.buildAbsoluteUrl(event.pathImage),
          pathProgram: this.buildAbsoluteUrl(event.pathProgram)
        }));
       
        // Extract total count from first event if available
        const totalCount = res.response[0]?.totalEvents || res.totalEvents || res.total;
        const totalPages = res.totalPages || (totalCount ? Math.ceil(totalCount / sizePage) : Math.ceil(events.length / sizePage));
       
        return {
          events,
          totalPages,
          totalCount
        };
      }
      console.warn('⚠️ Réponse inattendue API searchStudent:', res);
      return { events: [], totalPages: 1 };
    }),
    catchError(err => {
      console.error('Erreur API searchStudentEvents:', err);
      return throwError(() => new Error('Erreur lors de la récupération des événements.'));
    })
  );
}


  // 🔹 Lister les réservations d’un élève
  listReservationsByStudent(nameOrg: string, idStudent: number): Observable<Reservation[]> {
    const url = `${this.DOMAINE_URL}listReservationByStudent`;
    const body = { nameOrg, idStudent };

    return this.http.post<{ response: Reservation[] }>(url, body).pipe(
      map(res => res.response ?? []),
      catchError(() =>
        throwError(() => new Error('Erreur lors de la récupération des réservations.'))
      )
    );
  }

  // 🔹 Supprimer une réservation
  deleteReservation(idReservation: number): Observable<string> {
    if (!idReservation) {
      return throwError(() => new Error('ID de réservation invalide.'));
    }

    const url = `${this.DOMAINE_URL}deleteReservation/${idReservation}`;
    return this.http.get<{ response: string }>(url).pipe(
      map(res => res.response),
      catchError(() =>
        throwError(() => new Error('Erreur lors de la suppression de la réservation.'))
      )
    );
  }

  // 🔹 Fonction utilitaire : corrige les chemins relatifs
  private buildAbsoluteUrl(path?: string): string | undefined {
    if (!path) return undefined;
    if (path.startsWith('http')) return path;
    return `${this.apiOrigin}/${path.replace(/^\/+/, '')}`;
  }
}

